import React, { useState, useRef, useLayoutEffect, useCallback, useEffect, useMemo, } from 'react';
import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { Menu } from '@sas/nova-commons/components/Menu';
import { useResizeObserverRef } from '@sas/nova-commons/hooks/useResizeObserver';
import { useRefArray } from '@sas/nova-commons/hooks/useRefArray';
import useForceUpdate from '@sas/nova-commons/hooks/useForceUpdate';
import { useI18n } from '../../locale/I18nContext';
import { exposeTestId } from '../../config';
import { MenuButton } from '@sas/nova-commons/components/MenuButton';
import cx from 'classnames';
import { OverflowLite } from '@sas/react-svg';
import { renderInlineCssVars, useDesignTokens } from '@sas/nova-commons/theme';
import { useLatestRef } from '../hooks';
import cn from './ObjectToolbar.css.json';
import './ObjectToolbar.css';
export const ObjectToolbar = (props) => {
    var _a, _b, _c;
    const [menuState, setMenuState] = useState({
        isOpen: false,
    });
    const localize = useI18n();
    const { refs: buttonRefs, createSetRef } = useRefArray();
    function closeMenu() {
        var _a, _b;
        if (!menuState.isOpen) {
            return;
        }
        setMenuState({ isOpen: false });
        (_a = props.onClose) === null || _a === void 0 ? void 0 : _a.call(props);
        (_b = buttonRefs[focusedIndex]) === null || _b === void 0 ? void 0 : _b.focus();
    }
    function openMenu(item, index) {
        var _a;
        setMenuState({
            isOpen: true,
            focusFirstItem: refEventSource.current === 'keyboard',
            targetId: item.id,
            buttonIndex: index,
        });
        const button = buttonRefs[index];
        if (!button) {
            return;
        }
        (_a = props.onOpen) === null || _a === void 0 ? void 0 : _a.call(props);
    }
    const { createTargetRef: createButtonRef, rootRef: wrapperRef } = useIntersectionObserver((entries) => {
        for (const { intersectionRatio, target } of entries) {
            target.style.visibility = intersectionRatio === 1 ? 'visible' : 'hidden';
        }
    }, { threshold: 1 });
    let setWrapperRef;
    const refChildrenContainer = useRef(null);
    const [childrenHidden, setChildrenHidden] = useState(false);
    const [maxButtonCount, setMaxButtonCount] = useState(undefined);
    {
        function updateChildrenVisibility() {
            const parent = getParentRef();
            const child = refChildrenContainer.current;
            if (!child || !parent) {
                setChildrenHidden(false);
                return;
            }
            setChildrenHidden(child.getBoundingClientRect().bottom + 2 > parent.getBoundingClientRect().bottom);
        }
        useLayoutEffect(updateChildrenVisibility);
        const [setParentRef, getParentRef] = useResizeObserverRef((entry) => {
            closeMenu();
            updateChildrenVisibility();
            // Calculate maximum number of buttons that can fit in the toolbar
            const border = 2;
            const positionBuffer = 4;
            const buttonHeight = 28;
            const maxButtonCount = Math.max(1, // always render at least one button
            Math.floor((entry.target.getBoundingClientRect().height - border - positionBuffer) / buttonHeight));
            setMaxButtonCount(maxButtonCount);
        });
        setWrapperRef = useCallback((e) => {
            var _a;
            wrapperRef(e);
            setParentRef((_a = e === null || e === void 0 ? void 0 : e.parentElement) !== null && _a !== void 0 ? _a : null);
        }, [setParentRef, wrapperRef]);
    }
    const refEventSource = useRef(undefined);
    let toolbarItems;
    const [focusedIndex, setFocusedIndex] = useState(0);
    const refAutofocus = useRef();
    {
        // Iterate through props.items to determine how many button items we have.
        // This is done upfront to preserve the order of items in the overflow menu.
        /** Number of items to render as buttons, excluding overflow */
        let itemRenderCount;
        /** Number of items in the overflow menu */
        let overflowCount;
        {
            let buttonItemCount = 0;
            let alwaysHasOverflow = props.alwaysShowOverflow;
            // forEach since for..of incorrectly causes hook warnings later in the file.
            props.items.forEach((item) => {
                if (item.type === 'button' && !item.button.preferOverflow) {
                    buttonItemCount++;
                }
                else if (item.type === 'overflow') {
                    alwaysHasOverflow = true;
                }
            });
            const totalButtonCount = buttonItemCount + (alwaysHasOverflow ? 1 : 0);
            itemRenderCount =
                maxButtonCount && totalButtonCount > maxButtonCount
                    ? maxButtonCount - 1 /* -1 for overflow */
                    : buttonItemCount;
            overflowCount = props.items.length - itemRenderCount;
        }
        // Split props.items into toolbar buttons and overflow items
        let buttonItems;
        let overflowSections;
        {
            buttonItems = [];
            overflowSections = [{ items: [] }];
            props.items.forEach((item) => {
                if (item.type === 'overflow') {
                    overflowSections[overflowSections.length - 1].items.push(item);
                }
                else if (item.type === 'button') {
                    if ((!item.button.preferOverflow && buttonItems.length < itemRenderCount) ||
                        (item.button.preferOverflow && overflowCount === 1 && !props.alwaysShowOverflow)) {
                        buttonItems.push(item);
                    }
                    else {
                        overflowSections[overflowSections.length - 1].items.push(item);
                    }
                }
                else if (item.type === 'separator') {
                    overflowSections.push({ items: [] });
                }
            });
            // Remove empty sections
            overflowSections = overflowSections.filter((section) => section.items.length > 0);
        }
        // Add overflow button item if needed
        const hasOverflow = overflowSections.length > 0 || props.alwaysShowOverflow;
        if (hasOverflow) {
            const text = props.accessibilityLabel
                ? localize({
                    key: 'ObjectToolbarIcon.OverflowButton.labeled.fmt',
                    args: { label: props.accessibilityLabel },
                })
                : localize({
                    key: 'ObjectToolbarIcon.OverflowButton.default.txt',
                });
            buttonItems.unshift({
                type: 'button',
                id: 'objectToolbar-overflow',
                text,
                subSections: overflowSections,
                button: {
                    icon: OverflowLite,
                },
            });
        }
        toolbarItems = buttonItems;
        // Find the button to autofocus on the first render
        // FIXME: Broken when button moves to overflow
        if (refAutofocus.current === undefined) {
            refAutofocus.current = false;
            let index = -1;
            if (props.autofocusId) {
                index = buttonItems.findIndex((item) => item.id === props.autofocusId);
                if (index === -1 &&
                    overflowSections.some(({ items }) => items.some(({ id }) => id === props.autofocusId))) {
                    // The autofocus id exists in the overflow menu
                    index = 0;
                }
                if (index !== -1) {
                    refAutofocus.current = true;
                    setFocusedIndex(index);
                }
            }
        }
    }
    const menuSections = menuState.targetId
        ? (_a = toolbarItems.find(({ id }) => id === menuState.targetId)) === null || _a === void 0 ? void 0 : _a.subSections
        : undefined;
    const refToolbar = useRef(null);
    useEffect(() => {
        var _a, _b;
        if (!refAutofocus.current && !((_a = refToolbar.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
            return;
        }
        refAutofocus.current = false;
        (_b = buttonRefs[focusedIndex]) === null || _b === void 0 ? void 0 : _b.focus();
    }, [focusedIndex, buttonRefs]);
    useEffect(() => {
        var _a;
        if ((_a = refToolbar.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) {
            return;
        }
        setFocusedIndex(0);
    }, [toolbarItems.length]);
    const dt = useDesignTokens();
    const buttonPaneTokens = renderInlineCssVars({ bg: dt.reportTokens.backgrounds.page.bg }, 'button-pane');
    const renderToolbar = toolbarItems.length > 0 || !!((_b = props.buttons) === null || _b === void 0 ? void 0 : _b.length);
    const forceUpdate = useForceUpdate();
    return renderToolbar ? (
    // extra wrapper div because Button's onPress does not provide event source information
    React.createElement("div", { className: cx(cn.wrapper, props.className), style: Object.assign({ opacity: props.visible || menuState.isOpen ? undefined : 0 }, props.style), 
        // using onCapture so ref is set before onPress is called
        onClickCapture: () => {
            refEventSource.current = 'mouse';
        }, onKeyDownCapture: () => {
            refEventSource.current = 'keyboard';
        }, ref: setWrapperRef, "data-testid": exposeTestId() ? `${props.id}-object-toolbar` : null }, (_c = props.buttons) === null || _c === void 0 ? void 0 :
        _c.map((buttonData, index) => (React.createElement("div", { key: index, className: cn.buttonPane, style: Object.assign(Object.assign({}, buttonPaneTokens), { visibility: 'hidden' }), ref: createButtonRef(index) }, buttonData.type === 'menubutton' ? (React.createElement(MenuButton, Object.assign({}, buttonData, { className: cn.enablePointer, icon: buttonData.icon && { icon: buttonData.icon } }))) : (React.createElement(Button, Object.assign({}, buttonData, { className: cn.enablePointer, icon: buttonData.icon && { icon: buttonData.icon } })))))),
        toolbarItems.length <= 0 ? null : (React.createElement("div", { className: cn.toolbarContainer },
            React.createElement("div", { ref: refToolbar, className: cx(cn.toolbar, cn.enablePointer), role: "toolbar", style: renderInlineCssVars(ObjectToolbarTokens(dt), 'object-toolbar'), onKeyDown: (e) => {
                    let handled = true;
                    const lastIndex = toolbarItems.length - 1;
                    switch (e.key) {
                        case 'ArrowUp':
                        case 'ArrowLeft':
                            setFocusedIndex((i) => (i > 0 ? i - 1 : lastIndex));
                            break;
                        case 'ArrowDown':
                        case 'ArrowRight':
                            setFocusedIndex((i) => (i < lastIndex ? i + 1 : 0));
                            break;
                        case 'PageUp':
                        case 'Home':
                            setFocusedIndex(0);
                            break;
                        case 'PageDown':
                        case 'End':
                            setFocusedIndex(lastIndex);
                            break;
                        default:
                            handled = false;
                    }
                    if (handled) {
                        e.preventDefault();
                    }
                } }, toolbarItems.map((item, i) => {
                var _a, _b;
                return (React.createElement(Button, { key: item.id, ref: createSetRef(i), className: cn.button, variant: ButtonVariant.Icon, icon: { icon: item.button.icon }, onPress: () => {
                        var _a;
                        setFocusedIndex(i);
                        if (item.subSections) {
                            openMenu(item, i);
                        }
                        else {
                            (_a = item.onSelected) === null || _a === void 0 ? void 0 : _a.call(item);
                        }
                    }, tooltip: (_a = item.button.tooltip) !== null && _a !== void 0 ? _a : item.text, ariaLabel: (_b = item.button.tooltip) !== null && _b !== void 0 ? _b : item.text, "aria-haspopup": item.subSections ? true : undefined, "aria-expanded": item.subSections ? menuState.targetId === item.id : undefined, focusable: focusedIndex === i }));
            })),
            React.createElement(Menu, { isOpen: menuState.isOpen &&
                    // menuSections may be empty if items are added on open when using alwaysShowOverflow, but
                    // Menu calls onDismiss when it is open with no menu sections. This prevents isOpen from
                    // being set to true until the dynamically added items are available.
                    !!(menuSections === null || menuSections === void 0 ? void 0 : menuSections.length), defaultFocusLocation: menuState.focusFirstItem ? [0, 0] : undefined, position: {
                    dockingElement: typeof menuState.buttonIndex === 'number'
                        ? buttonRefs[menuState.buttonIndex]
                        : undefined,
                    horizontalAlign: 'end',
                    verticalAlign: 'end',
                }, sections: menuSections !== null && menuSections !== void 0 ? menuSections : [], onItemSelected: (itemPath) => {
                    var _a, _b, _c;
                    let item;
                    let sections = menuSections;
                    for (const [sectionIndex, itemIndex] of itemPath) {
                        item = (_b = (_a = sections === null || sections === void 0 ? void 0 : sections[sectionIndex]) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b[itemIndex];
                        sections = item === null || item === void 0 ? void 0 : item.subSections;
                    }
                    if (!item) {
                        console.error('[ObjectToolbar]: invalid item picked');
                        return;
                    }
                    (_c = item.onSelected) === null || _c === void 0 ? void 0 : _c.call(item);
                    if (!item.keepOpen) {
                        closeMenu();
                    }
                    else {
                        // NOVACOMMONS-25043: item can't be clicked multiple times without forcing a rerender
                        forceUpdate();
                    }
                }, onDismiss: closeMenu }),
            props.children ? (React.createElement("div", { ref: refChildrenContainer, className: cx(!childrenHidden && cn.enablePointer), style: { visibility: childrenHidden ? 'hidden' : undefined } }, props.children)) : null)))) : null;
};
function ObjectToolbarTokens(dt) {
    return {
        bd: dt.reportTokens.stateStandard.normal.bd,
        bg: dt.reportTokens.backgrounds.page.bg,
    };
}
// TODO: If this hook gets reused, make sure callbackRef.current is never called when stale
function useIntersectionObserver(callback, options) {
    const { createSetRef, refs } = useRefArray();
    const callbackRef = useLatestRef(callback);
    const rootElementRef = useRef(null);
    const refIntersectionObserver = useRef();
    const createIntersectionObserver = useCallback((root) => {
        if (!root) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            var _a;
            (_a = callbackRef.current) === null || _a === void 0 ? void 0 : _a.call(callbackRef, entries);
        }, { root, threshold: options.threshold });
        for (const ref of refs) {
            if (!ref) {
                continue;
            }
            observer.observe(ref);
        }
        return observer;
    }, [options.threshold, refs, callbackRef]);
    useEffect(() => {
        refIntersectionObserver.current = createIntersectionObserver(rootElementRef.current);
        return () => {
            var _a;
            (_a = refIntersectionObserver.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            refIntersectionObserver.current = undefined;
        };
    }, [createIntersectionObserver]);
    return useMemo(() => ({
        createTargetRef: (index) => (element) => {
            var _a, _b;
            const oldElement = refs[index];
            if (oldElement) {
                (_a = refIntersectionObserver.current) === null || _a === void 0 ? void 0 : _a.unobserve(oldElement);
            }
            if (element) {
                (_b = refIntersectionObserver.current) === null || _b === void 0 ? void 0 : _b.observe(element);
            }
            createSetRef(index)(element);
        },
        rootRef: (el) => {
            var _a;
            (_a = refIntersectionObserver.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            rootElementRef.current = el;
            refIntersectionObserver.current = createIntersectionObserver(rootElementRef.current);
        },
    }), [refs, createSetRef, createIntersectionObserver]);
}
